import { ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Popover,
  Row,
  Segmented,
  Skeleton,
  Space,
} from 'antd';
import dayjs from 'dayjs';
import {
  type CSSProperties,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import MessengerDetail from '../components/messenger/detail';
import MessengerList from '../components/messenger/list';
import MessengerMessages from '../components/messenger/messages';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { useSocketSubscription } from '../hooks/use-socket-subscription';
import { type RootState } from '../reducers';
import {
  adminConnected,
  adminDisconnected,
  isLeadEngaged,
  loadLeads,
  pruneLeads,
} from '../reducers/messenger/reducer';
import { ELeadFilter } from '../reducers/messenger/types';
import Socket from '../socket';
import { SPACING } from '../theme/tokens';

const styleMessengerTab: CSSProperties = {
  height: 'calc(100vh - 9.3rem)',
  padding: `0 ${SPACING.xs}px`,
  overflowY: 'scroll',
};

const HOUR_INTERVALS: number[] = [1, 2, 6, 12, 24];

const PRUNE_LEADS_INTERVAL_MS = 60 * 1000;

const SECONDS_IN_HOUR = 60 * 60;

function MessengerContainer({
  socket = undefined,
}: {
  socket?: Socket;
}): ReactElement {
  const dispatch = useAppDispatch();
  const messengerLeads = useSelector((s: RootState) => s.Messenger.leads);
  const messengerLastMessageUuid = useSelector(
    (s: RootState) => s.Messenger.lastMessage.uuid,
  );
  const userUuid = useSelector((s: RootState) => s.User.uuid);

  const [hours, setHours] = useState<number>(1);
  const [leadFilter, setLeadFilter] = useState<ELeadFilter>(
    ELeadFilter.ENGAGED,
  );

  const engagedLeadsCount: number = useMemo(
    (): number => (messengerLeads ?? []).filter(isLeadEngaged).length,
    [messengerLeads],
  );

  const leadFilterOptions = useMemo(
    () => [
      {
        label: `Engaged (${engagedLeadsCount})`,
        value: ELeadFilter.ENGAGED,
      },
      {
        label: `All (${messengerLeads?.length ?? 0})`,
        value: ELeadFilter.ALL,
      },
    ],
    [engagedLeadsCount, messengerLeads?.length],
  );

  const handleHourSelect = useCallback((interval: number): void => {
    setHours(interval);
  }, []);

  const handleReload: () => void = useCallback((): void => {
    dispatch(loadLeads(hours));
  }, [dispatch, hours]);

  const hourButtons = useMemo(
    () =>
      HOUR_INTERVALS.map((interval: number): ReactElement => (
        <Popover
          trigger="hover"
          key={interval}
          title={`Reload leads ${interval} h. before.`}
          placement="top"
        >
          <Button
            size="small"
            type={hours === interval ? 'primary' : 'default'}
            onClick={(): void => handleHourSelect(interval)}
          >
            {interval}h.
          </Button>
        </Popover>
      )),
    [hours, handleHourSelect],
  );

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect((): (() => void) => {
    audioRef.current = new Audio('/static/juntos.mp3');

    return (): void => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect((): (() => void) => {
    const adminConnectedInterval: NodeJS.Timeout = setInterval(
      () => userUuid && dispatch(adminConnected(userUuid)),
      5000,
    );

    return (): void => {
      clearInterval(adminConnectedInterval);
      if (userUuid) {
        dispatch(adminDisconnected(userUuid));
      }
    };
  }, [dispatch, userUuid]);

  useEffect((): void => {
    if (audioRef.current && messengerLastMessageUuid) {
      audioRef.current.play().catch(() => {});
    }
  }, [messengerLastMessageUuid]);

  useEffect((): (() => void) => {
    const pruneLeadsInterval: NodeJS.Timeout = setInterval((): void => {
      dispatch(pruneLeads(dayjs().unix(), hours * SECONDS_IN_HOUR));
    }, PRUNE_LEADS_INTERVAL_MS);

    return (): void => {
      clearInterval(pruneLeadsInterval);
    };
  }, [dispatch, hours]);

  useSocketSubscription(
    socket,
    (s) => {
      s.subscribeEventMessenger();
      return () => s.unsubscribeEventMessenger();
    },
    [],
  );

  return (
    <Row gutter={SPACING.none}>
      <Col span={6} style={styleMessengerTab}>
        <Space size={SPACING.lg} align="start">
          {hourButtons}
          <Button
            type="primary"
            size="small"
            icon={<ReloadOutlined />}
            onClick={handleReload}
          >
            Reload
          </Button>
        </Space>

        <Divider />

        <Segmented
          block
          size="small"
          options={leadFilterOptions}
          value={leadFilter}
          onChange={(value: ELeadFilter): void => setLeadFilter(value)}
        />

        <Divider />

        {!messengerLeads && <Skeleton />}
        {messengerLeads && <MessengerList filter={leadFilter} />}
      </Col>

      <Col span={6} style={styleMessengerTab}>
        <MessengerMessages />
      </Col>

      <Col span={12} style={styleMessengerTab}>
        <MessengerDetail />
      </Col>
    </Row>
  );
}

export default MessengerContainer;
